// The space ladder as one state stands on it: nine rungs, each reached with a date and source or
// not. The victory track counts the same milestones (victory.ts: rungs / 9).
//
//   1 own orbital launch · 2 own crewed orbital flight · 3 lunar soft landing · 4 lunar sample
//   return · 5 Mars soft landing · 6 national crewed space station · 7 crewed lunar mission
//   (21st century) · 8 crewed lunar landing (21st century) · 9 crewed Mars landing
//   (docs/bake/indicators.md, "Space-race ladder")

import type { DateStr, Milestone, SourceId } from '../core/schema.ts';
import { SPACE_RUNGS } from './victory.ts';

const RUNG_LABELS: readonly string[] = [
  'own orbital launch',
  'own crewed orbital flight',
  'lunar soft landing',
  'lunar sample return',
  'Mars soft landing',
  'national crewed space station',
  'crewed lunar mission (21st century)',
  'crewed lunar landing (21st century)',
  'crewed Mars landing',
];

export interface SpaceRung {
  /** 1..9. */
  rung: number;
  label: string;
  /** The milestone that completed the rung by the date; null when not reached. */
  reached: { date: DateStr; title: string; sources: SourceId[] } | null;
}

export interface SpaceLadder {
  iso3: string;
  date: DateStr;
  reached: number;
  total: number;
  /** The lowest rung not reached yet; null when all are. */
  next: { rung: number; label: string } | null;
  rungs: SpaceRung[];
}

export function spaceLadder(milestones: Milestone[], iso3: string, date: DateStr): SpaceLadder {
  // Earliest milestone per rung, so a rung listed twice still counts once.
  const done = new Map<number, Milestone>();
  for (const m of milestones) {
    if (m.track !== 'space' || m.iso3 !== iso3 || m.rung === undefined || m.date > date) {
      continue;
    }
    const held = done.get(m.rung);
    if (!held || m.date < held.date) {
      done.set(m.rung, m);
    }
  }

  const rungs = RUNG_LABELS.map((label, i) => {
    const m = done.get(i + 1);
    return { rung: i + 1, label, reached: m ? { date: m.date, title: m.title, sources: m.sources } : null };
  });
  const open = rungs.find((r) => r.reached === null);
  return {
    iso3,
    date,
    reached: rungs.filter((r) => r.reached !== null).length,
    total: SPACE_RUNGS,
    next: open ? { rung: open.rung, label: open.label } : null,
    rungs,
  };
}
